import React, {HTMLAttributes} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, m, mb, mr, p, pointer} from "@pg-design/helpers-css";
import {calculateRemSize, gap} from "@pg-design/helpers-css-strings";
import {CheckIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRecommendedProperty} from "../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../property/types/PropertyBoxType";
import {getPropertyBoxMultileadText} from "./get_property_box_multilead_text";

type IProps = {
    property: IRecommendedProperty;
    checked: boolean;
    recommendationAdvantages?: {priceDifference: number; areaDifference: number};
    handleChange: (value: number) => void;
    onHover?: (propertyID: null | number) => void;
    type: PropertyBoxType;
    hidePrices?: boolean;
} & Omit<HTMLAttributes<HTMLLIElement>, "checked" | "onClick" | "onMouseEnter" | "onMouseLeave" | "property">;

interface IThemeProps {
    isActiveOnHover?: boolean;
    checked: boolean;
}

//To make whole container clickable = prevent event propagation from checkbox and
//meet TS types in Checkbox onChange function
const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
};

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyItem = (props: IProps) => {
    const {property, checked, handleChange, onHover, hidePrices, ...htmlProps} = props;
    const propertyID = property.id;
    const {isMobile} = useUserDevice();
    const areaDifference = props.recommendationAdvantages?.areaDifference || 0;
    const priceDifference = props.recommendationAdvantages?.priceDifference || 0;

    const handleOnHover = () => {
        onHover && onHover(propertyID);
    };

    const onMouseOut = () => {
        props.onHover && props.onHover(null);
    };

    const ariaLabel = property.id + "label";

    const theme = useTheme();

    return (
        <ListItemWrapper
            checked={checked}
            isActiveOnHover={!!props.onHover}
            onClick={() => handleChange(propertyID)}
            onMouseEnter={handleOnHover}
            onMouseLeave={onMouseOut}
            css={wrapperStyles}
            {...htmlProps}
        >
            <div css={imageAndDataContainer}>
                <Image
                    css={propertyImage}
                    src={property.offer?.main_image?.m_img_350x230}
                    width={isMobile ? "99px" : "123px"}
                    height={isMobile ? "119px" : "127px"}
                    ratio={isMobile ? {xs: "99/119"} : {xs: "123/127"}}
                    alt={`${property.offer.vendor.name}-${property.id}`}
                />

                <div css={propertyDataContainer}>
                    <Text
                        variant="info_txt_1"
                        id={ariaLabel}
                        strong
                    >{`${property.rooms} ${roomsPluralize(property.rooms)}, ${areaFormat(property.area)}`}</Text>
                    <Text color={theme.colors.gray[700]} variant="info_txt_3" className="bt">
                        {property.offer.name}, {property.offer.address}
                    </Text>
                    {property.price && !hidePrices && (
                        <Button css={buttonStyle} variant="highlight_primary" as="div">
                            {priceFormat(property.price)}
                        </Button>
                    )}
                    {props.type === PropertyBoxType.RECOMMENDED ? (
                        <>
                            {getPropertyBoxMultileadText(areaDifference, priceDifference) && (
                                <div css={recommendationContainer}>
                                    <Text variant="info_txt_2">{getPropertyBoxMultileadText(areaDifference, priceDifference)}</Text>
                                </div>
                            )}
                        </>
                    ) : (
                        <div css={mainPropertyContainer}>
                            <CheckIcon size="1.5" />
                            <Text variant="info_txt_2">Oglądana oferta</Text>
                        </div>
                    )}
                </div>
            </div>

            <CheckboxContainer checked={checked} isActiveOnHover={!!props.onHover} onClick={stopPropagation}>
                <Checkbox
                    id={`${property.id}`}
                    name={`property_${propertyID}`}
                    checked={checked}
                    value={propertyID}
                    onChange={() => handleChange(propertyID)}
                    aria-labelledby={ariaLabel}
                    checkedMarkColor="#02D054"
                />
            </CheckboxContainer>
        </ListItemWrapper>
    );
};

export const ListItemWrapper = styled.li<IThemeProps>`
    ${borderRadius()};
    ${pointer};
    width: 100%;
    position: relative;
    border: 1px solid ${(props) => props.theme.colors.gray[500]};
    max-width: calc(100% - 2.7rem); // 2.4rem (half of checkbox) + border

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        max-width: unset;
        flex-direction: row;
    }

    ${(props) =>
        props.checked &&
        css`
            border: 1px solid transparent;
            ${elevation(3)};
        `};

    ${(props) =>
        props.isActiveOnHover &&
        css`
            &:hover {
            }
        `}
`;

const imageAndDataContainer = css`
    display: flex;
`;

const propertyImage = (theme: Theme) => css`
    ${borderRadius(1, 0, 0, 1)}
    ${mr(1)};
    flex: 0 0 99px;
    overflow: hidden;
    width: 99px;
    height: 119px;

    @media (min-width: ${theme.breakpoints.md}) {
        width: auto;
        height: auto;
        flex: 0 0 123px;
    }
`;

const propertyDataContainer = (theme: Theme) => css`
    ${m(1, 1, 1, 0)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${gap(1)};

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 65%;
        padding: 0;
    }
`;

export const CheckboxContainer = styled.div<IThemeProps>`
    width: 4.8rem;
    height: 4.8rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => props.theme.colors.gray[100]};
    border: 1px solid ${(props) => props.theme.colors.gray[500]};
    position: absolute;
    right: 0rem;
    top: 50%;
    transform: translateY(-50%);
    bottom: unset;
    left: unset;

    @media (min-width: 375px) {
        right: -2.5rem;
    }

    ${(props) =>
        props.checked &&
        css`
            border: none;
            box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.19);
        `};
`;

const wrapperStyles = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(2)};
        max-width: 69.7rem;
    }
`;

const buttonStyle = css`
    justify-content: left;
`;

const recommendationContainer = css`
    background-color: #b9e19b;
    display: flex;
    align-items: center;
    border-radius: ${calculateRemSize(1)};
    ${p(0.5, 1.5, 0.5, 1.5)};
    max-width: max-content;
`;

const mainPropertyContainer = css`
    background-color: #e1d7cd;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: ${calculateRemSize(1)};
    gap: ${calculateRemSize(2)};
    ${p(0.5, 1.5, 0.5, 1.5)};
    max-width: max-content;
`;
import React, {HTMLAttributes} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, m, mb, mr, p, pointer} from "@pg-design/helpers-css";
import {calculateRemSize, gap} from "@pg-design/helpers-css-strings";
import {CheckIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRecommendedProperty} from "../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../property/types/PropertyBoxType";
import {getPropertyBoxMultileadText} from "./get_property_box_multilead_text";

type IProps = {
    property: IRecommendedProperty;
    checked: boolean;
    recommendationAdvantages?: {priceDifference: number; areaDifference: number};
    handleChange: (value: number) => void;
    onHover?: (propertyID: null | number) => void;
    type: PropertyBoxType;
    hidePrices?: boolean;
} & Omit<HTMLAttributes<HTMLLIElement>, "checked" | "onClick" | "onMouseEnter" | "onMouseLeave" | "property">;

interface IThemeProps {
    isActiveOnHover?: boolean;
    checked: boolean;
}

//To make whole container clickable = prevent event propagation from checkbox and
//meet TS types in Checkbox onChange function
const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
};

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyItem = (props: IProps) => {
    const {property, checked, handleChange, onHover, hidePrices, ...htmlProps} = props;
    const propertyID = property.id;
    const {isMobile} = useUserDevice();
    const areaDifference = props.recommendationAdvantages?.areaDifference || 0;
    const priceDifference = props.recommendationAdvantages?.priceDifference || 0;

    const handleOnHover = () => {
        onHover && onHover(propertyID);
    };

    const onMouseOut = () => {
        props.onHover && props.onHover(null);
    };

    const ariaLabel = property.id + "label";

    const theme = useTheme();

    return (
        <ListItemWrapper
            checked={checked}
            isActiveOnHover={!!props.onHover}
            onClick={() => handleChange(propertyID)}
            onMouseEnter={handleOnHover}
            onMouseLeave={onMouseOut}
            css={wrapperStyles}
            {...htmlProps}
        >
            <div css={imageAndDataContainer}>
                <Image
                    css={propertyImage}
                    src={property.offer?.main_image?.m_img_350x230}
                    width={isMobile ? "99px" : "123px"}
                    height={isMobile ? "119px" : "127px"}
                    ratio={isMobile ? {xs: "99/119"} : {xs: "123/127"}}
                    alt={`${property.offer.vendor.name}-${property.id}`}
                />

                <div css={propertyDataContainer}>
                    <Text
                        variant="info_txt_1"
                        id={ariaLabel}
                        strong
                    >{`${property.rooms} ${roomsPluralize(property.rooms)}, ${areaFormat(property.area)}`}</Text>
                    <Text color={theme.colors.gray[700]} variant="info_txt_3" className="bt">
                        {property.offer.name}, {property.offer.address}
                    </Text>
                    {property.price && !hidePrices && (
                        <Button css={buttonStyle} variant="highlight_primary" as="div">
                            {priceFormat(property.price)}
                        </Button>
                    )}
                    {props.type === PropertyBoxType.RECOMMENDED ? (
                        <>
                            {getPropertyBoxMultileadText(areaDifference, priceDifference) && (
                                <div css={recommendationContainer}>
                                    <Text variant="info_txt_2">{getPropertyBoxMultileadText(areaDifference, priceDifference)}</Text>
                                </div>
                            )}
                        </>
                    ) : (
                        <div css={mainPropertyContainer}>
                            <CheckIcon size="1.5" />
                            <Text variant="info_txt_2">Oglądana oferta</Text>
                        </div>
                    )}
                </div>
            </div>

            <CheckboxContainer checked={checked} isActiveOnHover={!!props.onHover} onClick={stopPropagation}>
                <Checkbox
                    id={`${property.id}`}
                    name={`property_${propertyID}`}
                    checked={checked}
                    value={propertyID}
                    onChange={() => handleChange(propertyID)}
                    aria-labelledby={ariaLabel}
                    checkedMarkColor="#02D054"
                />
            </CheckboxContainer>
        </ListItemWrapper>
    );
};

export const ListItemWrapper = styled.li<IThemeProps>`
    ${borderRadius()};
    ${pointer};
    width: 100%;
    position: relative;
    border: 1px solid ${(props) => props.theme.colors.gray[500]};
    max-width: calc(100% - 2.7rem); // 2.4rem (half of checkbox) + border

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        max-width: unset;
        flex-direction: row;
    }

    ${(props) =>
        props.checked &&
        css`
            border: 1px solid transparent;
            ${elevation(3)};
        `};

    ${(props) =>
        props.isActiveOnHover &&
        css`
            &:hover {
            }
        `}
`;

const imageAndDataContainer = css`
    display: flex;
`;

const propertyImage = (theme: Theme) => css`
    ${borderRadius(1, 0, 0, 1)}
    ${mr(1)};
    flex: 0 0 99px;
    overflow: hidden;
    width: 99px;
    height: 119px;

    @media (min-width: ${theme.breakpoints.md}) {
        width: auto;
        height: auto;
        flex: 0 0 123px;
    }
`;

const propertyDataContainer = (theme: Theme) => css`
    ${m(1, 1, 1, 0)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${gap(1)};

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 65%;
        padding: 0;
    }
`;

export const CheckboxContainer = styled.div<IThemeProps>`
    width: 4.8rem;
    height: 4.8rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => props.theme.colors.gray[100]};
    border: 1px solid ${(props) => props.theme.colors.gray[500]};
    position: absolute;
    right: 0rem;
    top: 50%;
    transform: translateY(-50%);
    bottom: unset;
    left: unset;

    @media (min-width: 375px) {
        right: -2.5rem;
    }

    ${(props) =>
        props.checked &&
        css`
            border: none;
            box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.19);
        `};
`;

const wrapperStyles = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(2)};
        max-width: 69.7rem;
    }
`;

const buttonStyle = css`
    justify-content: left;
`;

const recommendationContainer = css`
    background-color: #b9e19b;
    display: flex;
    align-items: center;
    border-radius: ${calculateRemSize(1)};
    ${p(0.5, 1.5, 0.5, 1.5)};
    max-width: max-content;
`;

const mainPropertyContainer = css`
    background-color: #e1d7cd;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: ${calculateRemSize(1)};
    gap: ${calculateRemSize(2)};
    ${p(0.5, 1.5, 0.5, 1.5)};
    max-width: max-content;
`;
